import React, { useState, useEffect } from 'react';
import { ConversionTable, GradientInput, InputLabel, InputWrapper, InputGroup, TabContent } from '../../pages/tools/ToolsPage.components';
import { flowConversionFactors, formatNumber, normalizeNumericInput } from './conversionUtils';

const FlowConverter: React.FC = () => {
    const [cubicMeterHour, setCubicMeterHour] = useState('1');
    const [literHour, setLiterHour] = useState('');
    const [gallonHour, setGallonHour] = useState('');
    const [barrelHour, setBarrelHour] = useState('');

    // Initialize with default value on mount
    useEffect(() => {
        const defaultValue = 1;
        setLiterHour(formatNumber(defaultValue * flowConversionFactors.literHour, 6));
        setGallonHour(formatNumber(defaultValue * flowConversionFactors.gallonHour, 6));
        setBarrelHour(formatNumber(defaultValue * flowConversionFactors.barrelHour, 6));
    }, []);

    const handleCubicMeterHourChange = (value: string) => {
        value = normalizeNumericInput(value);
        setCubicMeterHour(value);
        if (value === '') {
            setLiterHour('');
            setGallonHour('');
            setBarrelHour('');
            return;
        }
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
            setLiterHour(formatNumber(numValue * flowConversionFactors.literHour, 6));
            setGallonHour(formatNumber(numValue * flowConversionFactors.gallonHour, 6));
            setBarrelHour(formatNumber(numValue * flowConversionFactors.barrelHour, 6));
        }
    };

    const handleLiterHourChange = (value: string) => {
        value = normalizeNumericInput(value);
        setLiterHour(value);
        if (value === '') {
            setCubicMeterHour('');
            setGallonHour('');
            setBarrelHour('');
            return;
        }
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
            const baseValue = numValue / flowConversionFactors.literHour;
            setCubicMeterHour(formatNumber(baseValue, 6));
            setGallonHour(formatNumber(baseValue * flowConversionFactors.gallonHour, 6));
            setBarrelHour(formatNumber(baseValue * flowConversionFactors.barrelHour, 6));
        }
    };

    const handleGallonHourChange = (value: string) => {
        value = normalizeNumericInput(value);
        setGallonHour(value);
        if (value === '') {
            setCubicMeterHour('');
            setLiterHour('');
            setBarrelHour('');
            return;
        }
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
            const baseValue = numValue / flowConversionFactors.gallonHour;
            setCubicMeterHour(formatNumber(baseValue, 6));
            setLiterHour(formatNumber(baseValue * flowConversionFactors.literHour, 6));
            setBarrelHour(formatNumber(baseValue * flowConversionFactors.barrelHour, 6));
        }
    };

    const handleBarrelHourChange = (value: string) => {
        value = normalizeNumericInput(value);
        setBarrelHour(value);
        if (value === '') {
            setCubicMeterHour('');
            setLiterHour('');
            setGallonHour('');
            return;
        }
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
            const baseValue = numValue / flowConversionFactors.barrelHour;
            setCubicMeterHour(formatNumber(baseValue, 6));
            setLiterHour(formatNumber(baseValue * flowConversionFactors.literHour, 6));
            setGallonHour(formatNumber(baseValue * flowConversionFactors.gallonHour, 6));
        }
    };

    return (
        <TabContent>
            <ConversionTable>
                <thead>
                    <tr>
                        <th>Flow</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Cubic Meter/Hour:</td>
                        <td>
                            <InputWrapper>
                                <InputGroup>
                                    <GradientInput
                                        id="cubicmeterperhour"
                                        type="text"
                                        value={cubicMeterHour}
                                        onChange={(e) => handleCubicMeterHourChange(e.target.value)}
                                    />
                                    <InputLabel htmlFor="cubicmeterperhour">m³/h</InputLabel>
                                </InputGroup>
                            </InputWrapper>
                        </td>
                    </tr>
                    <tr>
                        <td>Liter/Hour:</td>
                        <td>
                            <InputWrapper>
                                <InputGroup>
                                    <GradientInput
                                        id="literperhour"
                                        type="text"
                                        value={literHour}
                                        onChange={(e) => handleLiterHourChange(e.target.value)}
                                    />
                                    <InputLabel htmlFor="literperhour">l/h</InputLabel>
                                </InputGroup>
                            </InputWrapper>
                        </td>
                    </tr>
                    <tr>
                        <td>Gallon (US)/Hour:</td>
                        <td>
                            <InputWrapper>
                                <InputGroup>
                                    <GradientInput
                                        id="gallonperhour"
                                        type="text"
                                        value={gallonHour}
                                        onChange={(e) => handleGallonHourChange(e.target.value)}
                                    />
                                    <InputLabel htmlFor="gallonperhour">gal/h</InputLabel>
                                </InputGroup>
                            </InputWrapper>
                        </td>
                    </tr>
                    <tr>
                        <td>Barrel/Hour:</td>
                        <td>
                            <InputWrapper>
                                <InputGroup>
                                    <GradientInput
                                        id="barrelperhour"
                                        type="text"
                                        value={barrelHour}
                                        onChange={(e) => handleBarrelHourChange(e.target.value)}
                                    />
                                    <InputLabel htmlFor="barrelperhour">bbl/h</InputLabel>
                                </InputGroup>
                            </InputWrapper>
                        </td>
                    </tr>
                </tbody>
            </ConversionTable>
        </TabContent>
    );
};

export default FlowConverter;
